import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { InfoIcon, DollarSign, MapPin, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";

interface PricingSettings {
  gutter_5in_price_per_ft: number;
  gutter_6in_price_per_ft: number;
  downspout_price_each: number;
  gutter_guard_price_per_ft: number;
  overhead_percent: number;
  profit_margin_percent: number;
}

interface BuildingData {
  gutterLengthFt?: number;
  downspoutCount?: number;
}

interface GuttersEstimateProps {
  buildingData?: BuildingData | null;
}

export function GuttersEstimate({ buildingData }: GuttersEstimateProps) {
  const [settings, setSettings] = useState<PricingSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [gutterLength, setGutterLength] = useState<string>("");
  const [downspoutQty, setDownspoutQty] = useState<string>("");
  const [guardLength, setGuardLength] = useState<string>("");
  const [gutterSize, setGutterSize] = useState<"5in" | "6in">("5in");
  const [measuring, setMeasuring] = useState(false);
  const [autoMeasured, setAutoMeasured] = useState(false);
  
  const { user } = useAuth();

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      if (!user) return;

      const { data } = await supabase
        .from('pricing_settings')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (data) {
        setSettings(data as PricingSettings);
      }
    } catch (error) {
      console.error('Error loading pricing settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAutoMeasureFromMap = () => {
    if (!buildingData) {
      toast.error("No building measurement data available. Please run a measurement first.");
      return;
    }

    setMeasuring(true);
    
    // Use Solar API building data
    if (buildingData.gutterLengthFt) {
      setGutterLength(String(buildingData.gutterLengthFt));
    }
    
    if (buildingData.downspoutCount) {
      setDownspoutQty(String(buildingData.downspoutCount));
    }
    
    setMeasuring(false);
    setAutoMeasured(true);
    toast.success("Gutter measurements loaded from Solar API!");
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">Loading pricing...</p>
        </CardContent>
      </Card>
    );
  }

  if (!settings) {
    return (
      <Card>
        <CardContent className="pt-6">
          <Alert>
            <InfoIcon className="h-4 w-4" />
            <AlertDescription>
              Please set your pricing rates in{" "}
              <Link to="/settings?tab=pricing" className="font-medium underline">
                Settings → Pricing & Rates
              </Link>{" "}
              to unlock gutter estimates.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const gutterLengthNum = parseFloat(gutterLength) || 0;
  const downspoutQtyNum = parseInt(downspoutQty) || 0;
  const guardLengthNum = parseFloat(guardLength) || 0;

  const sizeRate = gutterSize === "5in" 
    ? settings.gutter_5in_price_per_ft 
    : settings.gutter_6in_price_per_ft;

  const gutterTotal = gutterLengthNum * sizeRate;
  const downspoutTotal = downspoutQtyNum * settings.downspout_price_each;
  const guardTotal = guardLengthNum * settings.gutter_guard_price_per_ft;
  const subtotalCost = gutterTotal + downspoutTotal + guardTotal;

  const overheadCost = subtotalCost * (settings.overhead_percent / 100);
  const costWithOverhead = subtotalCost + overheadCost;
  const profitAmount = costWithOverhead * (settings.profit_margin_percent / 100);
  const finalCustomerPrice = costWithOverhead + profitAmount;

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {/* Input Section */}
      <Card>
        <CardHeader>
          <CardTitle>Gutter Measurements</CardTitle>
          <CardDescription>Auto-measure or enter dimensions manually</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Auto-Measure Button */}
          <div className="pb-4 border-b">
            <Button
              variant="outline"
              className="w-full gap-2"
              onClick={handleAutoMeasureFromMap}
              disabled={!buildingData}
            >
              <MapPin className="w-4 h-4" />
              Auto-Measure From Map
            </Button>
          </div>
          <div className="space-y-2">
            <Label htmlFor="gutterSize">Gutter Size</Label>
            <Select value={gutterSize} onValueChange={(value: "5in" | "6in") => setGutterSize(value)}>
              <SelectTrigger id="gutterSize">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5in">5" Gutter</SelectItem>
                <SelectItem value="6in">6" Gutter</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="gutterLength">Total Gutter Length (ft)</Label>
            <Input
              id="gutterLength"
              type="number"
              placeholder="0"
              value={gutterLength}
              disabled={measuring}
              onChange={(e) => setGutterLength(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="downspoutQty">Downspouts (qty)</Label>
            <Input
              id="downspoutQty"
              type="number"
              placeholder="0"
              value={downspoutQty}
              disabled={measuring}
              onChange={(e) => setDownspoutQty(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="guardLength">Gutter Guard Length (ft) - Optional</Label>
            <Input
              id="guardLength"
              type="number"
              placeholder="0"
              value={guardLength}
              onChange={(e) => setGuardLength(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Estimate Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            Gutter Quick Estimate
          </CardTitle>
          <CardDescription>Ballpark pricing based on your rates</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Material & Labor Costs */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-muted-foreground">Material & Labor Costs</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {gutterSize === "5in" ? '5"' : '6"'} Gutter ({gutterLengthNum.toFixed(0)} ft)
                </span>
                <span className="font-medium">${gutterTotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Downspouts ({downspoutQtyNum})</span>
                <span className="font-medium">${downspoutTotal.toFixed(2)}</span>
              </div>
              {guardLengthNum > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Gutter Guard ({guardLengthNum.toFixed(0)} ft)</span>
                  <span className="font-medium">${guardTotal.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t">
                <span className="font-medium">Subtotal</span>
                <span className="font-semibold">${subtotalCost.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Business Costs & Profit */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-muted-foreground">Business Costs & Profit</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Overhead ({settings.overhead_percent}%)</span>
                <span className="font-medium">${overheadCost.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Profit Margin ({settings.profit_margin_percent}%)</span>
                <span className="font-medium">${profitAmount.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Final Customer Price */}
          <div className="pt-4 border-t">
            <div className="flex justify-between items-center">
              <span className="text-lg font-semibold">Customer Price</span>
              <span className="text-2xl font-bold text-primary">${finalCustomerPrice.toFixed(2)}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Includes ${profitAmount.toFixed(2)} profit
            </p>
          </div>

          <p className="text-xs text-muted-foreground pt-2 border-t">
            Adjust your rates in{" "}
            <Link to="/settings?tab=pricing" className="underline">
              Settings
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
